import { type FilterDefinition, FilterDefinitionTypeEnum, type FilterToggleDefinition, type FilterWithValue } from '../filters'

/**
 * Unreserved in RFC 3986, so the token crosses the query string unencoded, and
 * no option value can collide with it. Mirrors FilterToggle::PREFIX in PHP.
 */
export const FILTER_TOGGLE_PREFIX = '~'

/**
 * The values a filter carries, without the toggle tokens travelling along them.
 *
 * All tokens are dropped, not only a given one: a filter is free to declare more
 * than one toggle.
 */
export function stripToggleTokens(values: string[]): string[] {
  return values.filter(value => !String(value).startsWith(FILTER_TOGGLE_PREFIX))
}

/**
 * Whether the toggle is on in the values a filter carries, falling back to its
 * default when no token states it.
 */
export function readToggle(values: string[], toggle: FilterToggleDefinition): boolean {
  const token = `${FILTER_TOGGLE_PREFIX}${toggle.name}:`
  const stated = values.find(value => String(value).startsWith(token))

  return stated === undefined ? toggle.default : stated.slice(token.length) === '1'
}

/**
 * The values with the toggle state stated at their end, replacing any state they
 * already carried for that toggle.
 */
export function withToggleToken(
  values: string[],
  toggle: FilterToggleDefinition,
  isOn: boolean
): string[] {
  const token = `${FILTER_TOGGLE_PREFIX}${toggle.name}:`

  return [
    ...values.filter(value => !String(value).startsWith(token)),
    `${token}${isOn ? '1' : '0'}`,
  ]
}

/**
 * Extracts the raw filter value from the filters object
 */
export function getFilterRawValue(
  filters: Record<string, string | boolean>,
  filterName: string
): string | boolean | null {
  return filters[filterName] ?? null
}

/**
 * Converts a raw filter value to the format expected by FilterForm
 */
export function getFilterValueForForm(
  filter: FilterDefinition,
  rawValue: string | boolean | null
): string[] | boolean | string | null {
  if (rawValue == null) {
    if (filter.type === FilterDefinitionTypeEnum.BOOLEAN) return false
    return null
  }

  if ([FilterDefinitionTypeEnum.SELECT, FilterDefinitionTypeEnum.ASYNC_SELECT].includes(filter.type)) {
    return String(rawValue).split(',').filter(Boolean)
  }

  if (filter.type === FilterDefinitionTypeEnum.BOOLEAN) {
    return rawValue === '1'
  }

  return String(rawValue)
}

/**
 * Converts applied filters from the filters object to FilterWithValue format
 */
export function parseAppliedFilters(
  filters: Record<string, string | boolean>,
  availableFilters: FilterDefinition[]
): FilterWithValue[] {
  return Object.entries(filters)
    .filter(([name]) => availableFilters.some(f => f.name === name))
    .filter(([, value]) => value !== '')
    .map(([name, value]) => {
      const filterDefinition = availableFilters.find(f => f.name === name)
      if (!filterDefinition) {
        return null
      }

      let parsedValue: string | boolean | string[]

      if (filterDefinition.type === FilterDefinitionTypeEnum.BOOLEAN) {
        parsedValue = value === '1'
      } else if ([FilterDefinitionTypeEnum.SELECT, FilterDefinitionTypeEnum.ASYNC_SELECT].includes(filterDefinition.type)) {
        parsedValue = String(value).split(',').filter(v => v !== '')
      } else if ([FilterDefinitionTypeEnum.PERIOD, FilterDefinitionTypeEnum.TEXT].includes(filterDefinition.type)) {
        parsedValue = String(value)
      } else {
        throw Error(`Filter type not supported: ${filterDefinition.type}`)
      }

      return {
        filter: filterDefinition,
        value: parsedValue
      } as FilterWithValue
    })
    .filter(Boolean) as FilterWithValue[]
}

/**
 * Converts a FilterForm value to the storage format
 */
export function serializeFilterValue(
  filter: FilterDefinition,
  value: string[] | boolean | string
): string {
  if (filter.type === FilterDefinitionTypeEnum.BOOLEAN) {
    return value ? '1' : '0'
  }

  if ([FilterDefinitionTypeEnum.SELECT, FilterDefinitionTypeEnum.ASYNC_SELECT].includes(filter.type)) {
    return (value as string[]).join(',')
  }

  if ([FilterDefinitionTypeEnum.PERIOD, FilterDefinitionTypeEnum.TEXT].includes(filter.type)) {
    return String(value)
  }

  throw Error(`Filter type not supported: ${filter.type}`)
}

/**
 * Checks if a filter value is valid (not empty)
 */
export function isValidFilterValue(
  filter: FilterDefinition,
  value: string[] | boolean | string
): boolean {
  if (filter.type === FilterDefinitionTypeEnum.BOOLEAN) {
    return true // Boolean values are always valid
  }

  if ([FilterDefinitionTypeEnum.SELECT, FilterDefinitionTypeEnum.ASYNC_SELECT].includes(filter.type)) {
    // A toggle token alone states an option, not a filter: it narrows nothing.
    return Array.isArray(value) && stripToggleTokens(value).length > 0
  }

  if (filter.type === FilterDefinitionTypeEnum.PERIOD) {
    return typeof value === 'string' && value !== '' && value !== ':'
  }

  if (filter.type === FilterDefinitionTypeEnum.TEXT) {
    return typeof value === 'string' && value.trim() !== ''
  }

  return false
}

